import React from 'react';

import { Box, Button, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import { type Column, formatValue, toCsv } from './format';
import type { Grouped } from './model';

type Props = {
  title: string;
  dimensionLabel: string;
  rows: ReadonlyArray<Grouped>;
  columns: ReadonlyArray<Column>;
  range: string;
  fileName: string;
};

function download(name: string, contents: string) {
  const url = URL.createObjectURL(new Blob([contents], { type: 'text/csv;charset=utf-8;' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}

export default function LoyaltySocieteTable({ title, dimensionLabel, rows, columns, range, fileName }: Props) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ mb: 1 }}>
        <Box>
          <Typography variant="subtitle1">{title}</Typography>
          <Typography variant="caption" color="text.secondary">
            {range}
          </Typography>
        </Box>
        <Button size="small" onClick={() => download(fileName, toCsv(title, range, dimensionLabel, rows, columns))}>
          CSV
        </Button>
      </Stack>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{dimensionLabel}</TableCell>
              {columns.map((column) => (
                <TableCell key={column.key} align="right">
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.label} hover>
                <TableCell>{row.label}</TableCell>
                {columns.map((column) => (
                  <TableCell key={column.key} align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatValue(column.value(row.values), column.format)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={columns.length + 1}>No rows for this selection.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}
